import { useEffect, useRef, useState } from "react"
import { Search, Filter, Calendar, User, MessageSquare, ThumbsUp, Bookmark, Share2, Bell, Hash } from "lucide-react"
import CommunityBanner from "./CommunityBanner"
import { useCommunity } from "../../context/CommunityContext"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog"
import Button from "../common/Button"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"
import { X, Upload } from "lucide-react"

const categories = [
  { id: "all", name: "All Updates" },
  { id: "announcements", name: "Announcements" },
  { id: "events", name: "Events" },
  { id: "resources", name: "Resources" },
  { id: "community", name: "Community News" },
]


export default function CreateUpdateDialog() {
  const { createUpdate } = useCommunity();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: '',
    tags: '',
    featuredImage: null,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        setScrolled(contentRef.current.scrollTop > 300);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setForm({ ...form, featuredImage: file });
  };

  const handleSubmit = async () => {
    const { title, content, category, tags } = form;
    await createUpdate({ title, content, category, tags });
    setOpen(false);
    setForm({
      title: '',
      content: '',
      category: '',
      tags: '',
      featuredImage: null,
    });
  };

  return (
    <>
      {open ? (
        <div className="fixed inset-0 w-full h-full bg-white z-50 flex flex-col">
        {/* Sticky header that appears on scroll */}
        {scrolled && (
          <div className="fixed top-0 left-0 right-0 bg-white z-10 shadow-md">
            <div 
              className="flex items-center justify-between p-4"
              style={{
                backgroundImage: form.featuredImage ? `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url(${URL.createObjectURL(form.featuredImage)})` : '',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-transparent text-xl font-bold outline-none w-full"
                placeholder="Title of your post"
              />
              <div className="flex gap-2">
                <span className="text-sm text-gray-500">{form.category}</span>
                <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Publish
                </Button>
              </div>
            </div>
          </div>
        )}
  
        {/* Content area with scroll */}
        <div 
          ref={contentRef} 
          className="flex-1 overflow-auto pb-20"
        >
          {/* Featured image section */}
          <div className="relative w-full h-64 md:h-96 bg-gray-100 overflow-hidden">
            {form.featuredImage ? (
              <>
                <img
                  src={URL.createObjectURL(form.featuredImage)}
                  alt="Featured"
                  className="w-full h-full object-cover"
                />
                <Button 
                  onClick={() => setForm({ ...form, featuredImage: null })}
                  className="absolute top-4 right-4 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 h-auto"
                >
                  <X size={16} />
                </Button>
              </>
            ) : (
              <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer group">
                <Upload size={48} className="text-gray-400 group-hover:text-gray-600" />
                <span className="mt-2 text-gray-500 text-sm">Add featured image</span>
                <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </label>
            )}
          </div>
  
          {/* Main content */}
          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Close button at top */}
            <div className="flex justify-end mb-6">
              <Button 
                variant="ghost" 
                onClick={() => setOpen(false)}
                className="h-10 w-10 p-0 rounded-full"
              >
                <X size={20} />
              </Button>
            </div>
            
            {/* Title */}
            <h1>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full text-4xl font-bold outline-none border-none mb-8 placeholder-gray-400"
                placeholder="Title of your post"
              />
            </h1>
            
            {/* Content */}
            <p>
              <Textarea
                placeholder="Start writing your story..."
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="w-full min-h-[300px] text-lg outline-none border-none resize-none placeholder-gray-400"
              />
            </p>
  
            {/* Category and tags */}
            <div className="mt-12 space-y-6 pt-6 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <Input
                  placeholder="Category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full border-0 border-b border-gray-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-gray-400 text-base"
                />
              </div>
  
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <Input
                  placeholder="Tags (comma-separated)"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="w-full border-0 border-b border-gray-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-gray-400 text-base"
                />
              </div>
            </div>
          </div>
        </div>
  
        {/* Fixed bottom publish bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-between">
          <div className="space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setOpen(false)} 
              className="text-gray-600 hover:text-gray-800"
            >
              Close
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => {/* Save draft logic */}} 
              className="text-gray-700 hover:bg-gray-200"
            >
              Save draft
            </Button>
          </div>
          <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white px-8">
            Publish
          </Button>
        </div>
      </div>
      ) : (
      <Button onClick={(e) => setOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-8">
        Create Community update
      </Button>
      )}
    </>
  );
}


export function CommunityNewsletterPage() {
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [isSubscribed, setIsSubscribed] = useState(false)
  const communityCtx = useCommunity();

  return (
    <div className="space-y-6">
      <CommunityBanner />

      {/* Subscribe Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Stay Updated</h2>
          <p className="text-indigo-100 mb-6">
            Get the latest updates, announcements, and resources delivered directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
            {isSubscribed ? (
              <div className="flex items-center gap-2 px-6 py-3 bg-white/10 rounded-lg">
                <Bell className="w-5 h-5" />
                <span>You're subscribed to updates</span>
              </div>
            ) : (
              <>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full sm:w-auto px-4 py-3 rounded-lg bg-white/10 border border-white/20 placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/40"
                />
                <button
                  onClick={() => setIsSubscribed(true)}
                  className="w-full sm:w-auto px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
                >
                  Subscribe
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="search"
            placeholder="Search updates..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex gap-2">
          <select className="px-4 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option>Most Recent</option>
            <option>Most Popular</option>
            <option>Most Discussed</option>
          </select>
          <button className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>
      </div>


      {/* // Add this below the search bar in CommunityNewsletterPage */}
      {/* <div className="flex justify-end">
        <button
          onClick={() => setShowCreateUpdate(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Create Update
        </button>
      </div> */}

      {/* Dialog for creating update */}
      <CreateUpdateDialog />

      {/* Categories */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all text-sm
              ${
                selectedCategory === category.id
                  ? "bg-indigo-600 text-white"
                  : "bg-white hover:bg-gray-50 text-gray-700"
              }
            `}
          >
            <Hash className="w-4 h-4" />
            {category.name}
          </button>
        ))}
      </div>

      {/* Featured Update */}
      <article className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
        <div className="aspect-[2/1] relative">
          <img
            src="/placeholder.svg?height=400&width=800"
            alt="Featured update"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-3 text-white mb-2">
              <span className="px-3 py-1 rounded-full bg-indigo-600 text-sm font-medium">Announcement</span>
              <span className="flex items-center gap-1 text-sm">
                <Calendar className="w-4 h-4" /> June 1, 2024
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Introducing New Community Features</h2>
            <p className="text-gray-200 line-clamp-2">
              We're excited to announce the launch of our latest features that will enhance your community experience...
            </p>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">By Community Team</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">24 comments</span>
              </div>
              <div className="flex items-center gap-2">
                <ThumbsUp className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-500">156 likes</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Bookmark className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Regular Updates */}
      <div className="grid gap-6">
        {communityCtx.updates && communityCtx.updates.data.updates.map((update) => (
          <article
            key={update._id}
            className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-gray-100 text-sm font-medium">
                  {update.category}
                </span>
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" /> {update.createdAt}
                </span>
              </div>

              <h3 className="text-xl font-bold mb-2 hover:text-indigo-600 transition-colors">
                {update.title}
              </h3>

              <p className="text-gray-600 mb-4">
                {update.content}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-500">12 comments</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ThumbsUp className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-500">48 likes</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Bookmark className="w-5 h-5" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Load More */}
      <div className="text-center">
        <button className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-gray-50 transition-colors">
          Load More
        </button>
      </div>
    </div>
  )
}

